<template>
  <div
    class="audio-card"
    :style="{
      height: isMedicalIndustry === 1 ? '66px' : '94px',
      width: isMobile() && isMedicalIndustry === 1 ? '240px' : '100%'
    }"
  >
    <div class="audio-card__audio">
      <audio
        ref="audioRef"
        :src="audioUrl"
        @ended="handleEnded"
        @loadedmetadata="handleDurationChange"
        @timeupdate="handleTimeupdate"
      ></audio>
    </div>
    <div class="audio-card__info">
      <div class="audio-card__header">
        <div v-if="isMedicalIndustry === 1" class="medical">
          <div class="medical__first">
            <i class="icon-checkbox-circle-fill t-iconfont"></i>
            <span class="medical__first-time">{{ formatTime(addTime) }}</span>
          </div>
          <span class="medical__second">本次拜访已完成</span>
        </div>
        <template v-else>
          <img alt="" :src="AudioLogo" />
          <div class="audio-info">
            <div class="title">{{ formatTime(addTime) }}</div>
            <div class="desc" @click="handleDownload">
              {{ isMobile() ? '复制下载链接' : '下载' }}
            </div>
          </div>
        </template>
      </div>
      <template v-if="isMedicalIndustry !== 1">
        <div v-if="uploading" class="audio-card__content">
          <div class="progress-bar">
            <div
              id="progress"
              ref="radioProgress"
              class="progress"
              :style="{ width: uploadingPercent + '%' }"
            ></div>
          </div>
        </div>
        <div v-else class="audio-card__content">
          <div v-if="audioStatus === 'pause'" class="audio-card__play">
            <i class="icon-play-circle-line t-iconfont" @click="handlePlay"></i>
          </div>
          <div v-else class="audio-card__play">
            <i class="icon-pause-circle-line t-iconfont" @click="handlePlay"></i>
          </div>
          <div class="progress-bar">
            <div id="progress" ref="radioProgress" class="progress"></div>
          </div>
          <span id="current-time" class="text-gray-600">{{ currentTime }} </span>
          <span class="text-gray-600">/</span>
          <span id="duration" class="text-gray-600">{{ fileDuration }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from 'vue'
import AudioLogo from '@/assets/audio.png'
import xbb from '@xbb/xbb-utils'
import { isMobile } from '@/utils'
// import axios from 'axios'
import xbbSdk from 'xbb-sdk'
import AppMessage from '@/components/message/index.js'
import dayjs from 'dayjs'
import utils from '@/utils'

const props = defineProps({
  file: {
    type: [String, File],
    default: null
  },
  uploading: {
    type: Boolean,
    default: false
  },
  uploadingPercent: {
    type: Number,
    default: 0
  },
  addTime: {
    type: Number,
    default: 0
  },
  // 是否医疗行业(0 非医疗行业 1 医疗行业)
  isMedicalIndustry: {
    type: Number,
    default: 0
  }
})

const audioRef = ref(null)
const radioProgress = ref(null)
const fileDuration = ref('00:00:00')
const currentTime = ref('00:00:00')

const audioUrl = ref('')
let audioObjectUrl = ''

const updateAudioUrl = (file) => {
  if (audioObjectUrl) URL.revokeObjectURL(audioObjectUrl)
  audioObjectUrl = ''
  audioUrl.value = typeof file === 'string' ? file : file ? URL.createObjectURL(file) : ''
  if (typeof file !== 'string') audioObjectUrl = audioUrl.value
}

watch(() => props.file, updateAudioUrl, { immediate: true })
onBeforeUnmount(() => {
  if (audioObjectUrl) URL.revokeObjectURL(audioObjectUrl)
})

const audioStatus = ref('pause')

const formatDuration = (duration) => {
  if (!duration) return '00:00:00'
  const hours = Math.floor(duration / 3600)
  const minutes = Math.floor((duration % 3600) / 60)
  const seconds = Math.floor(duration % 60)
  return [hours, minutes, seconds].map((val) => val.toString().padStart(2, '0')).join(':')
}

const handleDurationChange = () => {
  console.log('handleDurationChange')
  fileDuration.value = formatDuration(audioRef.value.duration)
}

function handleTimeupdate() {
  const percent = (audioRef.value.currentTime / audioRef.value.duration) * 100
  radioProgress.value.style.width = percent + '%'
  currentTime.value = formatDuration(audioRef.value.currentTime)
}

function handleEnded() {
  audioStatus.value = 'pause'
  radioProgress.value.style.width = '0%'
  currentTime.value = '00:00:00'
}

function formatTime(timestamp) {
  return xbb.formatTime(dayjs(timestamp * 1000), 'yyyy-MM-dd HH:mm')
}

const handlePlay = () => {
  if (audioRef.value.paused) {
    audioRef.value.play()
    audioStatus.value = 'play'
  } else {
    audioRef.value.pause()
    audioStatus.value = 'pause'
  }
}

const handleDownload = () => {
  if (isMobile()) {
    let url = audioUrl.value
    // 链接处理

    const platform = utils.getPlatform(utils.xbbLocalStorageGet('corpid'))
    if (platform === 'dd') {
      if (url.startsWith('xbbProPrd')) {
        url = `https://xbbprdpro.xbongbong.com.cn/${url}`
      }
    } else if (url.startsWith('xbbProApp')) {
      url = `https://xbbprdapp.xbongbong.cn/${url}`
    }
    // 复制到剪切板
    utils.copyToClipboard(
      url,
      () => {
        AppMessage.success('已复制下载链接')
      },
      (type) => {
        AppMessage.error('复制失败', type)
      }
    )
  } else {
    xbbSdk.sendMessage('xbbDownloadFile', {
      url: audioUrl.value,
      name: `${formatTime(props.addTime)}.mp3`
    })
  }
}
</script>

<style scoped lang="less">
.audio-card {
  overflow: hidden;
  position: relative;
  border-radius: 8px;
  background-color: #fff;

  audio {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }

  .audio-card__info {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    padding: 12px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .upload-progress {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    .audio-card__play {
      cursor: pointer;
    }

    .audio-card__container {
      position: absolute;
      padding: 2px 4px;
      background-color: rgba(0, 0, 0, 0.5);
      color: #fff;
      border-radius: 4px;
      font-size: 12px;
    }

    .audio-card__info-type {
      top: 8px;
      right: 8px;
      background-color: rgba(0, 0, 0, 0.5);
      color: #fff;
    }

    .audio-card__info-size {
      bottom: 8px;
      left: 8px;
    }

    .audio-card__info-duration {
      bottom: 8px;
      right: 8px;
    }

    .audio-card__header {
      display: flex;

      img {
        height: 42px;
        width: 42px;
        margin-right: 12px;
      }

      .audio-info {
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .title {
          font-size: 14px;
          font-weight: 600;
          color: #323233;
        }

        .desc {
          cursor: pointer;
          font-size: 13px;
          color: #248df5;
        }
      }

      .medical {
        display: flex;
        flex-direction: column;
        font-family: PingFang SC;

        &__first {
          display: flex;
          align-items: center;

          .icon-checkbox-circle-fill {
            color: #00c241;
            margin-right: 8px;
          }

          &-time {
            font-family: inherit;
            font-size: 14px;
            font-weight: 600;
            line-height: normal;
            color: #323233;
          }
        }

        &__second {
          font-family: inherit;
          font-size: 13px;
          line-height: normal;
          color: #969799;
          margin: 4px 0 0 24px;
        }
      }
    }

    .audio-card__content {
      display: flex;
      align-items: center;
      height: 20px;

      .audio-card__play {
        .t-iconfont {
          font-size: 20px;
          color: #248df5;
          margin-right: 12px;
        }
      }

      .progress-bar {
        width: 100%;
        height: 6px;
        background-color: #ddd;
        border-radius: 5px;
        overflow: hidden;
        margin-right: 4px;
      }

      .progress {
        height: 100%;
        background-color: #007bff;
        width: 0%;
        transition: width 0.1s linear;
      }
    }
  }
}
</style>
